import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CheckoutQuestionsFlowDemo } from "./CheckoutQuestionsFlowDemo";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function Rule({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold tabular-nums text-primary-foreground">
          {n}
        </span>
        <p className="text-xs font-semibold text-foreground truncate">{title}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed pl-7">{children}</p>
    </div>
  );
}

export function CheckoutQuestionsRulesModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation("producerCheckoutQuestions");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent mobilePresentation="fullscreen" className="max-w-5xl overflow-hidden rounded-[28px] border-border/60 bg-card p-0 shadow-none sm:rounded-[28px]">
        {/* DialogTitle/Description de verdade: com <h2> solto o Radix loga erro
            de acessibilidade toda vez que o modal abre. */}
        <div className="px-6 pt-5 pb-3.5 border-b border-border/50">
          <DialogTitle className="font-display text-lg font-semibold tracking-tight text-foreground">
            {t("rulesModal.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-0.5">
            {t("rulesModal.description")}
          </DialogDescription>
        </div>

        <div className="max-h-[72vh] overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_324px] lg:grid-rows-[minmax(0,1fr)]">
          {/* Esquerda: storyboard animado */}
          <div className="p-4 lg:p-5 lg:flex lg:items-center lg:min-h-0">
            <CheckoutQuestionsFlowDemo />
          </div>

          {/* Direita: as regras em texto */}
          <div className="border-t lg:border-t-0 lg:border-l border-border/50 bg-secondary/15 lg:min-h-0 lg:overflow-y-auto">
            {/* Sem divisores entre as regras: cada um custa 13px de altura e o
                storyboard ao lado é quem define o tamanho do modal. */}
            <div className="p-4 space-y-3.5">
              <Rule n={1} title={t("rulesModal.rule1Title")}>
                {t("rulesModal.rule1Body1")}
                <span className="text-foreground">{t("rulesModal.rule1BodySpan")}</span>
                {t("rulesModal.rule1Body2")}
              </Rule>

              <Rule n={2} title={t("rulesModal.rule2Title")}>
                {t("rulesModal.rule2Body")}
              </Rule>

              <Rule n={3} title={t("rulesModal.rule3Title")}>
                {t("rulesModal.rule3Body")}
              </Rule>

              <Rule n={4} title={t("rulesModal.rule4Title")}>
                {t("rulesModal.rule4Body1")}
                <span className="text-foreground">–</span>
                {t("rulesModal.rule4Body2")}
              </Rule>

              <div className="flex items-start gap-2 p-2.5 rounded-xl border border-dashed border-border/60 bg-background/60">
                <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {t("rulesModal.infoBody1")}
                  <span className="text-foreground">{t("rulesModal.infoBodySpan")}</span>
                  {t("rulesModal.infoBody2")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-border/50 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">{t("rulesModal.footerNote")}</p>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-xl h-10 px-5 text-sm font-medium bg-primary text-primary-foreground shrink-0 hover:bg-primary/90 transition-[background-color,transform] duration-150 ease-out active:scale-[0.97]"
          >
            {t("rulesModal.button")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
